import React, { useEffect } from "react";
import { Route, Switch } from "react-router-dom";
import { Helmet } from "react-helmet";
import useSWR from "swr";

import { getProfile } from "../../../services/index";
import store from "../../../store";
import { RECEIVE_PROFILE } from "../../../constants/ActionTypes";

import Breadcrumbs from "../../new_template_common/breadcrumb/BreadCrumb";
import DashboardSidepanel from "./side-panel";
import MobileDashboardSidepanel from "./mobile-side-panel";

import Dashboard from "./dashboard";
import MyOrderDetails from "./my_orders";
import Profile from "./profile";
import ChangePassword from "./change_password";
import styles from "./dashboard-layout.module.scss";

const breadcrumbMap = {
  "/dashboard": "Dashboard",
  "/my-orders": "My Orders",
  "/user/profile": "Edit Profile",
  "/change-password": "Change Password",
};

const titleMap = {
  "/dashboard": "Dashboard",
  "/my-orders": "My Orders",
  "/user/profile": "Edit Profile",
  "/change-password": "Change Password",
};

const profileFetcher = () =>
  getProfile().then((response) =>
    response.data.success === true ? response.data.profile : null
  );

const DashboardLayout = (props) => {
  const { data } = useSWR(
    localStorage.getItem("api_token") ? "user-profile" : null,
    profileFetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      dedupingInterval: Infinity,
    }
  );

  useEffect(() => {
    if (!localStorage.getItem("api_token")) {
      props.history.push("/login");
    }
  }, []);

  useEffect(() => {
    if (data) {
      store.dispatch({ type: RECEIVE_PROFILE, payload: data });
    }
  }, [data]);

  const pathname = props.location.pathname;
  const pageTitle = titleMap[pathname] || "Dashboard";
  const isDashboardHome = pathname === "/dashboard";
  const showBreadcrumbOnMobile =
    pathname === "/my-orders" ||
    pathname === "/user/profile" ||
    pathname === "/change-password";
  const ancestor = isDashboardHome ? "Dashboard" : "Dashboard";
  const current = isDashboardHome ? null : breadcrumbMap[pathname] || null;

  return (
    <div className={`site-main ${styles.page}`}>
      <Helmet>
        <title>{pageTitle} | Diamond Jewellery Store - Sunny Diamonds</title>
        <meta
          name="description"
          content="Sunny diamonds one of the finest diamond jewellery store in India. Shop Internally Flawless diamond and platinum jewellery online at best price. Buy diamonds online!"
        />
        <meta
          name="keywords"
          content="Buy Diamonds online, Online diamonds shopping, Diamond Jewellery Store, Buy Diamond Rings Online, diamond earrings online, diamond pendants online kerala, buy diamond bangles online, buy diamond necklace, Buy wedding rings online, online diamond jewellery India"
        />
        <meta property="og:site_name" content="Sunny Diamonds" />
        <meta property="og:type" content="Summary" />
        <meta
          property="og:title"
          content="Buy Diamonds Online | Diamond Jewellery Store - Sunny Diamonds"
        />
        <meta
          property="og:description"
          content="Sunny diamonds one of the finest diamond jewellery store in India. Shop Internally Flawless diamond and platinum jewellery online at best price. Buy diamonds online!"
        />
        <meta property="og:url" content="https://sunnydiamonds.com/" />
        <meta name="twitter:card" content="Summary" />
        <meta name="twitter:site" content="@SunnyDiamond_4c" />
        <meta name="twitter:creator" content="Sunny Diamonds" />
        <meta
          name="twitter:title"
          content="Buy Diamonds Online | Diamond Jewellery Store - Sunny Diamonds"
        />
        <meta
          name="twitter:description"
          content="Sunny diamonds one of the finest diamond jewellery store in India. Shop Internally Flawless diamond and platinum jewellery online at best price. Buy diamonds online!"
        />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      <section className={styles.pageSection}>
        <div className={styles.pageSectionInner}>
          <div className="custom-container">
            <div
              className={`${styles.breadcrumbWrap} ${showBreadcrumbOnMobile ? styles.breadcrumbMobileVisible : ""}`}
            >
              <Breadcrumbs
                ancestor={ancestor}
                ancestorLink={!isDashboardHome ? "/dashboard" : undefined}
                current={current}
              />
            </div>

            <div
              className={`${styles.layout} ${isDashboardHome ? styles.layoutNoSidebar : ""}`}
            >
              <MobileDashboardSidepanel history={props.history} />

              <div
                className={`uk-width-1-4@m uk-visible@m p-0 ${styles.sidePanel} ${isDashboardHome ? styles.sidebarHidden : ""}`}
              >
                <div className={styles.stickyWrap}>
                  <DashboardSidepanel history={props.history} />
                </div>
              </div>

              <div
                className={`uk-width-expand@m ${styles.contentWrap} ${isDashboardHome ? styles.contentWrapFull : ""}`}
              >
                <Switch>
                  <Route
                    exact
                    path={`${process.env.PUBLIC_URL}/dashboard`}
                    component={Dashboard}
                  />
                  <Route
                    path={`${process.env.PUBLIC_URL}/my-orders`}
                    component={MyOrderDetails}
                  />
                  <Route
                    path={`${process.env.PUBLIC_URL}/user/profile`}
                    component={Profile}
                  />
                  <Route
                    path={`${process.env.PUBLIC_URL}/change-password`}
                    component={ChangePassword}
                  />
                </Switch>
              </div>
            </div>

          </div>
          {!isDashboardHome && (
            <section className={styles.helpSection}>
              <div className="custom-container">
                <div className={styles.helpInner}>
                  <div className={styles.helpHeader}>
                    <h2 className={styles.helpHeading}>Need Help?</h2>
                    <p className={styles.helpSubtext}>
                      Fast answers for orders, profile updates, and account access.
                    </p>
                  </div>
                  <div className={styles.helpGrid}>
                    <a
                      href="tel:+919744355555"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.helpCard}
                    >
                      <span
                        className={styles.helpIcon}
                        data-uk-icon="icon: receiver; ratio: 1.2"
                      ></span>
                      <div className={styles.helpCardBody}>
                        <div className={styles.helpLabel}>Call us</div>
                        <div className={styles.helpText}>
                          {window.customerServiceNumber.phone}
                        </div>
                        <div className={styles.helpMeta}>
                          Mon-Sat, 10:00 AM - 6:00 PM IST
                        </div>
                      </div>
                    </a>
                    <a
                      href="mailto:customerservice@sunnydiamonds.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.helpCard}
                    >
                      <span
                        className={styles.helpIcon}
                        data-uk-icon="icon: mail; ratio: 1.2"
                      ></span>
                      <div className={styles.helpCardBody}>
                        <div className={styles.helpLabel}>Email support</div>
                        <div className={styles.helpText}>
                          customerservice@sunnydiamonds.com
                        </div>
                        <div className={styles.helpMeta}>Replies within 24 hours</div>
                      </div>
                    </a>
                    <a
                      href="https://api.whatsapp.com/send?phone=+919961870099"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.helpCard}
                    >
                      <span
                        className={styles.helpIcon}
                        data-uk-icon="icon: whatsapp; ratio: 1.2"
                      ></span>
                      <div className={styles.helpCardBody}>
                        <div className={styles.helpLabel}>WhatsApp</div>
                        <div className={styles.helpText}>+91 9961 870 099</div>
                        <div className={styles.helpMeta}>
                          Quick chat for order updates
                        </div>
                      </div>
                    </a>
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>
      </section>
    </div>
  );
};

export default DashboardLayout;
